import { Box, Checkbox, Group, InputLabel } from "@mantine/core"
import { DefaultInfiniteList } from "../../../../-components/InfiniteList"
import { useDefaultSuspenseInfiniteQuery } from "../../../../utils/useDefaultSuspenseInfiniteQuery"
import { DefaultListItem } from "../../../../-components/default-list/list-item"
import { useDisclosure } from "@mantine/hooks"
import { useSearch, useNavigate } from "@tanstack/react-router"
import { HideableFilterGroup } from "../../-components/hideable-filter-group"
import { SearchTextInput } from "../../-components/search-text-input"
import { ResourceTypeTreeSelect } from "./ResourceTypeTreeSelect"
import {
  AddFieldFilterModal,
  FieldFilterButton,
} from "../../../../-components/field-data"
import useGetResourceObjectFieldTypes from "./useGetResourceObjectFieldTypes"
import useGetResourceFieldTypes from "./useGetResourceFieldTypes"
import { ResourceTypeCategory } from "../../../../@client/Theasoft_DataClasses_Dispo"
import {
  FieldFilterPill,
  getUpdatedFilterDict,
} from "../../../../-components/field-data"
import { SuspenseWithErrorBoundary } from "../../../../-components/suspense"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { useTranslation } from "react-i18next"
import { ResourceCard } from "./ResourceCard"
import { getPaginatedResourceObjects } from "@/orval/gen"
import {
  ResourceObjectFilter,
  ResourceObjectPageRequest,
} from "@/orval/gen/model"

export function ResourceObjectsCardList({
  category,
}: {
  category: ResourceTypeCategory
}) {
  const search = useSearch({ from: "/app/resources/resource-objects" })
  const { filter } = search
  const navigate = useNavigate()
  const [visible, setVisible] = useUserLocalStorage<boolean>({
    key: "resources-filter-visibility",
    defaultValue: true,
  })
  const { data: fieldTypes } = useGetResourceFieldTypes(category)
  const { data: objectFieldTypes } = useGetResourceObjectFieldTypes(category)
  const [modalOpened, { open, close }] = useDisclosure()
  const mergedFieldTypes = [...fieldTypes, ...objectFieldTypes]
  const { t } = useTranslation("app_server")

  const updateFilter = (newValue: ResourceObjectFilter) =>
    navigate({
      to: "/app/resources/resource-objects",
      search: { filter: { ...filter, ...newValue } },
      replace: true,
    })
  const updateSearch = (newValue: Partial<typeof search>) => {
    navigate({
      to: "/app/resources/resource-objects",
      search: { ...search, ...newValue },
      replace: true,
    })
  }

  React.useEffect(() => {
    updateFilter({ resourceTypeTreeNodes: undefined })
  }, [category])

  return (
    <>
      <Group pb={5} justify="space-between">
        <Group>
          <SearchTextInput
            autoFocus
            value={search.globalFilter ?? undefined}
            onChange={(e) => updateSearch({ globalFilter: e.target.value })}
          />
          <HideableFilterGroup align="end" visible={visible}>
            <ResourceTypeTreeSelect
              category={category}
              label={t("Type")}
              miw={250}
              onCheckedStateChange={(values) =>
                updateFilter({ resourceTypeTreeNodes: values })
              }
              initialCheckedState={filter?.resourceTypeTreeNodes ?? []}
            />
            <Box>
              <InputLabel>{t("Available")}</InputLabel>
              <Checkbox
                size="md"
                p={5}
                checked={filter?.available}
                onChange={() => updateFilter({ available: !filter?.available })}
              />
            </Box>
            {mergedFieldTypes.length > 0 && (
              <FieldFilterButton onClick={open} />
            )}
            {mergedFieldTypes.map(
              (type) =>
                !!filter?.fieldFilter?.[type.id] && (
                  <FieldFilterPill
                    typeName={type.name}
                    filter={filter.fieldFilter[type.id]}
                    onRemove={() => {
                      if (filter.fieldFilter) {
                        const { [type.id]: _, ...rest } = filter.fieldFilter

                        updateFilter({ fieldFilter: rest })
                      }
                    }}
                  />
                )
            )}
          </HideableFilterGroup>
        </Group>
        <HideableFilterGroup.Button
          visible={visible}
          onChange={(e) => setVisible(!e)}
          activeFilters={
            Object.keys(filter?.fieldFilter ?? {}).length +
            (filter?.resourceTypeTreeNodes?.length ? 1 : 0)
          }
        />
      </Group>
      <SuspenseWithErrorBoundary>
        <ResourceObjectsList
          request={{
            ...search,
            filter: { ...filter, category },
          }}
        />
      </SuspenseWithErrorBoundary>
      <AddFieldFilterModal
        opened={modalOpened}
        onClose={close}
        fieldTypes={mergedFieldTypes}
        onAdd={(record) => {
          updateFilter({
            fieldFilter: getUpdatedFilterDict(
              record.type,
              record.value,
              filter?.fieldFilter
            ),
          })
        }}
        values={filter?.fieldFilter}
      />
    </>
  )
}

function ResourceObjectsList({
  request,
}: {
  request: Omit<ResourceObjectPageRequest, "pageSize" | "pageParam">
}) {
  const navigate = useNavigate()
  const { t } = useTranslation("app_server")
  const [expanded, setExpanded] = React.useState(false)
  return (
    <DefaultInfiniteList
      w={"100%"}
      justify="space-evenly"
      queryResult={useDefaultSuspenseInfiniteQuery(
        getPaginatedResourceObjects,
        (pageRequest) => ({
          ...pageRequest,
          ...request,
        })
      )}
      renderItem={({ entity }) => (
        <DefaultListItem
          maw={"100%"}
          onClick={() =>
            navigate({
              to: "/app/resources/$id",
              params: { id: entity.resourceId.toString() },
            })
          }
        >
          <ResourceCard
            title={[entity.name, entity.type].join(" / ")}
            attachments={entity.attachments}
            resourceId={entity.resourceId}
            defaultFields={[
              { label: t("Condition"), value: entity.condition },
              { label: t("Code"), value: entity.identificationCode },
            ]}
            fieldData={entity.resourceFields.concat(entity.objectFields)}
            expanded={expanded}
            onExpandedChange={setExpanded}
          />
        </DefaultListItem>
      )}
      direction={"row"}
      wrap={"wrap"}
    />
  )
}
